<template>
	<v-card class="d-flex flex-column pb-5">
		<v-toolbar color="primary" dark flat>
			<v-toolbar-title style="flex: 0 1 auto">{{ title }}</v-toolbar-title>
			<!-- Paper search lives in the top bar (white solo pill), matching the
			     project + QC dashboard toolbars. -->
			<v-text-field
				v-model="searchKey"
				prepend-inner-icon="mdi-magnify"
				placeholder="Search papers..."
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				clearable
				bg-color="white"
				class="flex-grow-0 ml-4 cet-toolbar-search"
			/>
			<!-- Project filter. The table is server-paginated, so the chosen
			     project is applied server-side (sent as `projectUuid` to
			     /data-entry/search), not derived from the current page. Shown on
			     both the admin and "mine" pages (the toolbar is not mode-gated). -->
			<v-autocomplete
				v-model="selectedProjectUuid"
				:items="projectOptions"
				item-title="name"
				item-value="uuid"
				placeholder="Filter by project"
				density="compact"
				variant="solo"
				flat
				rounded
				hide-details
				hide-no-data
				auto-select-first
				clearable
				bg-color="white"
				class="flex-grow-0 ml-2 cet-toolbar-filter"
			/>
		</v-toolbar>

		<v-dialog
			v-model="invalidDialog"
			max-width="800px"
			transition="fade-transition"
		>
			<v-card>
				<v-toolbar color="primary" flat>
					<v-toolbar-title>Invalid Data Extraction</v-toolbar-title>
					<v-hover-icon class="mr-3" />
				</v-toolbar>

				<v-card-text
					class="pa-5 d-flex flex-sm-nowrap align-center justify-center validation-header-text"
				>
					Format Error: {{ validationData.message }}
				</v-card-text>

				<v-divider />

				<data-entry-validation-table
					:validation-data="validationData"
				></data-entry-validation-table>
				<v-card-actions class="py-5">
					<v-spacer />

					<v-btn class="bg-secondary" elevation="2" @click="hideInvalidDialog">
						Ok
					</v-btn>

					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog
			v-model="reopenDialog"
			max-width="800px"
			transition="fade-transition"
		>
			<v-card>
				<v-toolbar color="primary" flat>
					<v-toolbar-title>
						{{
							reassignOnly
								? "Reassign Source Data Extraction"
								: "Re-open Source Data Extraction"
						}}
					</v-toolbar-title>
					<v-hover-icon class="mr-3" />
				</v-toolbar>

				<v-card-text class="px-6 pt-4 pb-0">
					<div class="text-h6 text-center font-weight-medium mb-4">
						{{
							reassignOnly
								? "Who should this source data extraction be assigned to?"
								: "Are you sure you want to re-open this source data extraction?"
						}}
					</div>
					<v-sheet class="pa-4 my-3 rounded info-sheet" elevation="0">
						<div class="d-flex align-center mb-4">
							<v-icon class="me-2" color="primary">
								mdi-file-document-outline
							</v-icon>
							<span class="text-body-1 font-weight-bold">
								{{ selectedDataEntry.paper?.name }}
							</span>
						</div>
						<div class="d-flex align-center">
							<v-icon class="me-2" color="primary"> mdi-account </v-icon>
							<span class="text-body-2">
								Current assignee:
								{{ selectedDataEntry.user.email }}
							</span>
						</div>
					</v-sheet>
					<div v-if="showReassign" class="mb-4">
						<user-search
							v-model="selectedReassignUser"
							:multiple="false"
							:exclude-admin="true"
							:omit-deleted="true"
							label="Reassign to user"
						/>
					</div>
					<div class="text-center font-weight-medium mb-4">
						<template v-if="reassignOnly">
							This extraction is already reopened for rework. It will be
							assigned to
						</template>
						<template v-else>The extraction will be assigned to</template>
						<span class="font-weight-bold">
							{{
								selectedReassignUser
									? selectedReassignUser.email
									: selectedDataEntry.user.email
							}}</span
						><template v-if="reassignOnly">.</template>
						<template v-else>
							and moves back to rework until it is marked completed again.
						</template>
					</div>
				</v-card-text>

				<v-card-actions class="py-5">
					<v-spacer />
					<v-btn
						class="bg-secondary"
						elevation="2"
						:disabled="!selectedReassignUser || reassignUnchanged"
						@click="reopen"
					>
						Confirm
					</v-btn>
					<v-btn class="bg-grey" elevation="2" @click="hideReopenDialog">
						Cancel
					</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-data-table-server
			v-model:options="options"
			class="completed-extractions-table"
			item-key="uuid"
			:headers="headers"
			:items="extractions"
			:loading="loading"
			:items-length="totalExtractions"
		>
			<template #item.name="{ item }">
				{{ item.name }}
				<v-chip
					v-if="mode !== 'all' && item.dataEntries?.[0]?.superseded_at"
					size="x-small"
					variant="flat"
					prepend-icon="mdi-restart"
					class="ml-2 bg-surface-tint text-fg-2"
					:title="chipTitle(item.dataEntries[0])"
				>
					Superseded
				</v-chip>
			</template>
			<template #item.dataEntries="{ item }">
				<div class="d-flex flex-column align-center">
					<!-- DCTL-2257: a reopened extraction is the same fact the SDE and
					manager boards call rework - data_entries.reopened_at drives all
					three - so it gets the shared rework treatment (rust tint + ink +
					mdi-arrow-u-left-top) instead of this table's own bg-red-accent-1.
					That hex was a Vuetify Material class rather than a workflow colour,
					and pale red at 1.6:1 against the chip's own text carried the state
					by colour alone. -->
					<v-chip
						v-for="dataEntry of item.dataEntries"
						:key="dataEntry.uuid"
						variant="flat"
						class="ma-1 align-center custom-chip"
						:class="chipClass(dataEntry)"
						:prepend-icon="chipIcon(dataEntry)"
						:title="chipTitle(dataEntry)"
					>
						<div class="chip-content">
							<div class="chip-text">
								<div class="chip-header">
									{{ dataEntry.user.email }}
								</div>
								<div v-if="dataEntry.original_extractor" class="chip-header">
									({{ dataEntry.original_extractor.email }})
								</div>
								<div class="chip-subheader">
									{{ getCompletedTimestamp(dataEntry) }}
								</div>
								<div
									v-if="dataEntry.superseded_at"
									class="chip-subheader font-weight-medium"
								>
									Superseded
								</div>
							</div>
							<div v-if="dataEntry.is_validated" class="chip-icons">
								<v-tooltip
									v-if="showDownload"
									location="bottom"
									open-delay="500"
								>
									<template #activator="{ props: tooltipProps }">
										<v-icon
											v-bind="tooltipProps"
											class="chip-button ml-3"
											@click="download(dataEntry)"
										>
											mdi-download
										</v-icon>
									</template>
									<span>Download Extraction</span>
								</v-tooltip>

								<v-tooltip
									v-if="showExcelExport"
									location="bottom"
									open-delay="500"
								>
									<template #activator="{ props: tooltipProps }">
										<v-icon
											v-bind="tooltipProps"
											:class="{
												'chip-button': true,
												'ml-1': showDownload,
												'ml-3': !showDownload,
											}"
											@click="exportExcel([dataEntry.uuid], dataEntry)"
										>
											mdi-file-excel
										</v-icon>
									</template>
									<span>Export to Excel</span>
								</v-tooltip>

								<v-tooltip location="bottom" open-delay="500">
									<template #activator="{ props: tooltipProps }">
										<v-icon
											v-bind="tooltipProps"
											:class="{
												'chip-button': true,
												'ml-1': showDownload || showExcelExport,
												'ml-3': !showDownload && !showExcelExport,
											}"
											@click="goToDataViewer(dataEntry)"
										>
											mdi-magnify
										</v-icon>
									</template>
									<span>Open Tree View</span>
								</v-tooltip>
								<v-tooltip location="bottom" open-delay="500">
									<template #activator="{ props }">
										<v-hover v-slot="{ hover }">
											<v-icon
												v-bind="props"
												class="chip-button ml-1"
												:color="hover ? 'secondary' : ''"
												@click="startReadOnlyDataEntry(dataEntry)"
											>
												mdi-file-eye
											</v-icon>
										</v-hover>
									</template>
									<span>Open Read-only Extraction</span>
								</v-tooltip>
								<v-tooltip
									v-if="
										!dataEntry.superseded_at &&
										(!dataEntry.reopened_at || showReassign)
									"
									location="bottom"
									open-delay="500"
								>
									<template #activator="{ props: tooltipProps }">
										<v-icon
											v-bind="tooltipProps"
											class="chip-button ml-1"
											@click="openReopenDialog(dataEntry)"
										>
											mdi-account-arrow-left-outline
										</v-icon>
									</template>
									<span>
										{{
											dataEntry.reopened_at
												? "Reassign Extraction"
												: "Re-open Extraction"
										}}
									</span>
								</v-tooltip>

								<v-menu v-if="useExtractionEventHistory" location="bottom">
									<template #activator="{ props }">
										<v-tooltip location="bottom" open-delay="500">
											<template #activator="{ props: tooltipProps }">
												<v-icon
													v-bind="{
														...props,
														...tooltipProps,
													}"
													class="chip-button ml-1"
												>
													mdi-dots-vertical
												</v-icon>
											</template>
											<span>More actions</span>
										</v-tooltip>
									</template>
									<v-list density="compact">
										<v-list-item @click="showHistory(dataEntry)">
											<template #prepend>
												<v-icon size="small"> mdi-history </v-icon>
											</template>
											<v-list-item-title>
												Extraction History
											</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
							<div v-else class="chip-icons">
								<v-tooltip location="bottom" open-delay="500">
									<template #activator="{ props: tooltipProps }">
										<v-icon v-bind="tooltipProps" class="ml-2" color="error">
											mdi-text-box-remove
										</v-icon>
									</template>
									<span>Extraction is invalid</span>
								</v-tooltip>
							</div>
						</div>
					</v-chip>
				</div>
			</template>
			<template #item.projects="{ item }">
				<v-chip
					v-for="project of item.projects"
					:key="project.uuid"
					size="small"
					variant="flat"
					class="bg-surface-tint text-fg-1"
				>
					{{ project.name }}
				</v-chip>

				<v-tooltip
					v-if="!item.projects.length"
					location="bottom"
					open-delay="500"
				>
					<template #activator="{ props }">
						<v-icon v-bind="props" color="error" class="cursor-default">
							mdi-alert-circle
						</v-icon>
					</template>

					<span>No associated projects</span>
				</v-tooltip>
			</template>

			<template v-if="showDownload" #item.downloadAll="{ item }">
				<v-tooltip
					v-if="!hasValidExtraction(currentEntries(item))"
					location="bottom"
					open-delay="500"
				>
					<template #activator="{ props }">
						<v-icon v-bind="props" class="mr-2" color="error">
							mdi-text-box-remove
						</v-icon>
					</template>
					<span>No valid extractions</span>
				</v-tooltip>
				<v-tooltip v-else location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="chip-button mr-2"
								:color="hover ? 'secondary' : ''"
								size="large"
								@click="downloadAll(item)"
							>
								mdi-download
							</v-icon>
						</v-hover>
					</template>
					<span>Download All</span>
				</v-tooltip>
				<v-tooltip
					v-if="hasValidExtraction(currentEntries(item))"
					location="bottom"
					open-delay="500"
				>
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="chip-button mr-2"
								:color="hover ? 'secondary' : ''"
								size="large"
								@click="exportAllExcel(item)"
							>
								mdi-file-excel
							</v-icon>
						</v-hover>
					</template>
					<span>Export All to Excel</span>
				</v-tooltip>
			</template>

			<template #item.actions="{ item }">
				<v-tooltip
					v-if="isAdmin && !item.dataEntries[0]?.superseded_at"
					location="bottom"
					open-delay="500"
				>
					<template #activator="{ props: tooltipProps }">
						<v-icon
							v-bind="tooltipProps"
							class="chip-button"
							size="large"
							@click="openReopenDialog(item.dataEntries[0])"
						>
							mdi-account-arrow-left-outline
						</v-icon>
					</template>
					<span>Re-open</span>
				</v-tooltip>
				<v-tooltip location="bottom" open-delay="500">
					<template #activator="{ props }">
						<v-hover v-slot="{ hover }">
							<v-icon
								v-bind="props"
								class="chip-button ml-1"
								:color="hover ? 'secondary' : ''"
								size="large"
								@click="startReadOnlyDataEntry(item.dataEntries[0])"
							>
								mdi-file-eye
							</v-icon>
						</v-hover>
					</template>
					<span>Open Read-only Extraction</span>
				</v-tooltip>
			</template>
		</v-data-table-server>

		<extraction-history-modal
			v-if="useExtractionEventHistory"
			ref="historyModal"
			:data-entry-uuid="selectedHistoryUuid"
			@close="selectedHistoryUuid = null"
		/>
	</v-card>
</template>

<script>
import DataEntryValidationTable from "@/pages/DataEntry/DataEntryValidationTable";
import VHoverIcon from "@/components/VHoverIcon";
import UserSearch from "@/pages/UserManager/UserSearch";
import ExtractionHistoryModal from "@/components/ExtractionHistoryModal";
import _ from "lodash";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import { filenameFromContentDisposition } from "@/utils/contentDisposition";
import { WORKFLOW_STATES } from "@/constants/workflowStates";

export default {
	name: "CompletedExtractionTable",
	components: {
		DataEntryValidationTable,
		VHoverIcon,
		UserSearch,
		ExtractionHistoryModal,
	},

	props: { mode: { type: String, default: "all" } },

	setup() {
		const useExtractionEventHistory = useLDFlag("extraction-event-history");
		return {
			useExtractionEventHistory,
		};
	},

	data() {
		return {
			loading: false,
			options: {
				itemsPerPage: 10,
				page: 1,
			},
			extractions: [],
			invalidDialog: false,
			isAdmin: false,
			reopenDialog: false,
			title: "Completed Data Extractions",
			totalExtractions: 0,
			searchKey: "",
			// Server-side project filter (see getCompletedExtractions).
			selectedProjectUuid: null,
			projectOptions: [],
			selectedDataEntry: null,
			selectedReassignUser: null,
			selectedHistoryUuid: null,
			showDownload: false,
			showExcelExport: false,
			showReassign: false,
			validationData: null,
		};
	},

	computed: {
		// Colour + glyph for a reopened extraction, from the one shared workflow
		// map (DCTL-2257).
		reworkChip() {
			return WORKFLOW_STATES.rework;
		},

		// An extraction already back in rework has nothing to reopen: the reopen
		// endpoint writes no event for it and only moves the assignment
		// (`isAlreadyOpen`, DCTL-2360), so the dialog offers exactly that.
		reassignOnly() {
			return !!this.selectedDataEntry?.reopened_at;
		},

		// Reassigning to the current assignee would change nothing.
		reassignUnchanged() {
			return (
				this.reassignOnly &&
				this.selectedReassignUser?.uuid === this.selectedDataEntry?.user?.uuid
			);
		},

		headers() {
			return [
				{
					title: "Paper name",
					value: "name",
					width: "30%",
				},
				{
					title: "Projects",
					value: "projects",
					align: "center",
					width: "150px",
				},
				...(this.mode === "all"
					? [
							{
								title: "Completed Extractions",
								value: "dataEntries",
								align: "center",
								width: "250px",
							},
							...(this.showDownload
								? [
										{
											title: "Download All",
											value: "downloadAll",
											align: "center",
											width: "95px",
											sortable: false,
										},
									]
								: []),
						]
					: [
							{
								title: "Actions",
								value: "actions",
								align: "center",
								width: "80px",
								sortable: false,
							},
						]),
			];
		},
		user() {
			return useMainStore().user;
		},
	},
	watch: {
		options: {
			deep: true,
			handler() {
				this.getCompletedExtractions();
			},
		},
		mode() {
			this.options.page = 1;
			this.getCompletedExtractions();
		},
		searchKey(newVal, oldVal) {
			if (newVal?.length >= 2 || oldVal?.length >= 2) {
				this.options.page = 1;
				this.getCompletedExtractions();
			}
		},
		selectedProjectUuid() {
			this.options.page = 1;
			this.getCompletedExtractions();
		},
	},

	async mounted() {
		this.isAdmin = useMainStore().isAdmin;
		this.showDownload = useMainStore().can("download_data");
		// DCTL-2238: the XLSX export follows the permissions that already grant
		// access to the extraction, mirroring the widened route gate on
		// POST /data-entry/export-xlsx. Kept separate from showDownload so this
		// does NOT also surface the raw-JSON "Download Extraction" affordance,
		// which stays on download_data.
		this.showExcelExport = useMainStore().canAny([
			"download_data",
			"source_data_extraction",
			"quality_control",
		]);
		this.showReassign = useMainStore().can("reassign_extractions");
		this.getProjects();
	},

	methods: {
		...mapActions(useMainStore, [
			"setPaper",
			"setSaveUUID",
			"showNotification",
		]),
		// Full org-scoped project list for the toolbar filter (server-side
		// filter, so it is independent of the current result page). Mirrors
		// ProjectSearch's getAllProjects call.
		getProjects() {
			this.$http
				.get("/projects", {
					params: {
						omitDeleted: true,
						omitPapers: true,
						sortBy: ["name"],
						sortDesc: ["desc"],
					},
				})
				.then((response) => {
					this.projectOptions = response.data.projects.data;
				})
				.catch(() => {
					this.showNotification({
						message: "Could not load the project filter.",
						status: "error",
					});
				});
		},
		// DCTL-XXXX (DHELP-437): an extraction retired by "Restart extraction"
		// stays listed (muted, labelled Superseded) so the earlier work is
		// still viewable, but it is no longer the paper's current extraction:
		// no reopen / reassign (the server refuses both), and the paper-level
		// "all" download and export skip it.
		currentEntries(item) {
			return (item.dataEntries || []).filter(
				(dataEntry) => !dataEntry.superseded_at,
			);
		},
		chipClass(dataEntry) {
			if (dataEntry.superseded_at) return "bg-surface-tint text-fg-2";
			if (dataEntry.reopened_at) {
				return `bg-${this.reworkChip.tint} text-${this.reworkChip.ink}`;
			}
			return "bg-surface-tint text-fg-1";
		},
		chipIcon(dataEntry) {
			if (dataEntry.superseded_at) return "mdi-restart";
			return dataEntry.reopened_at ? this.reworkChip.icon : undefined;
		},
		chipTitle(dataEntry) {
			if (dataEntry.superseded_at) {
				return "Superseded - retired when this paper's extraction was restarted from scratch. View only.";
			}
			return dataEntry.reopened_at
				? "Reopened - back with the curator for rework"
				: undefined;
		},
		hasValidExtraction(dataEntries) {
			return dataEntries.some((dataEntry) => dataEntry.is_validated);
		},
		getCompletedTimestamp(item) {
			return new Date(this.completedTimestamp(item)).toLocaleString();
		},
		getCompletedExtractions() {
			this.loading = true;
			this.extractions = [];

			const { filter, itemsPerPage, page, sortBy, sortDesc } = this.options;

			const params = {
				done: true,
				...(this.mode === "all"
					? { excludeAdmin: true }
					: { currentUser: true }),
				filter: filter,
				includeJson: false,
				itemsPerPage: itemsPerPage,
				page: page,
				searchKey:
					this.searchKey && this.searchKey.length >= 2 ? this.searchKey : "",
				sortBy: sortBy,
				sortDesc: sortDesc,
				...(this.selectedProjectUuid
					? { projectUuid: this.selectedProjectUuid }
					: {}),
			};

			this.$http
				.get("/data-entry/search", { params: params })
				.then((response) => {
					this.extractions = response.data.papers.data;

					// Add paper name to each data entry
					this.extractions.forEach((item) => {
						const paperName = item.name || "Unnamed Paper";
						item.dataEntries.forEach((dataEntry) => {
							dataEntry.paper = _.omit(item, "dataEntries");
							dataEntry.paper.name = paperName;
						});
					});

					this.totalExtractions = parseInt(response.data.papers.meta.total, 10);
				})
				.catch((error) => {
					if (error.response) {
						if (error.response.data.message) {
							this.showNotification(error.response.data);
						} else {
							this.showNotification(error.response.data.error);
						}
					}
				})
				.finally(() => {
					this.loading = false;
				});
		},
		download(item) {
			this.$http
				.post(`/data-entry/download`, {
					uuids: [item.uuid],
				})
				.then((response) => {
					if (response.data.status === "error") {
						this.validationData = response.data.data;
						this.invalidDialog = true;
					} else {
						this.downloadJson(response.data.data, item);
					}
				})
				.catch((error) => {
					this.showNotification(error.response.data);
				});
		},
		downloadAll(item) {
			this.$http
				.post(`/data-entry/download`, {
					uuids: this.currentEntries(item)
						.filter((dataEntry) => dataEntry.is_validated)
						.map((dataEntry) => dataEntry.uuid),
				})
				.then((response) => {
					if (response.data.status === "error") {
						this.validationData = response.data.data;
						this.invalidDialog = true;
					} else {
						this.downloadJson(response.data.data, item.dataEntries[0]);
					}
				})
				.catch((error) => {
					this.showNotification(error.response.data);
				});
		},
		exportAllExcel(item) {
			const uuids = this.currentEntries(item)
				.filter((dataEntry) => dataEntry.is_validated)
				.map((dataEntry) => dataEntry.uuid);
			this.exportExcel(uuids, item.dataEntries[0]);
		},
		exportExcel(uuids, dataEntry) {
			if (!uuids.length) return;
			this.$http
				.post(`/data-entry/export-xlsx`, { uuids }, { responseType: "blob" })
				.then((response) => {
					const filename =
						filenameFromContentDisposition(
							response.headers["content-disposition"],
						) ||
						`${(dataEntry?.paper?.name || "extraction").replace(
							/[<>:"/\\|?*]/g,
							"-",
						)}.xlsx`;
					const url = URL.createObjectURL(response.data);
					const link = document.createElement("a");
					link.href = url;
					link.download = filename;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					URL.revokeObjectURL(url);
				})
				.catch(async (error) => {
					// A blob error body must be read back as text before showing.
					let payload = error.response?.data;
					if (payload instanceof Blob) {
						try {
							payload = JSON.parse(await payload.text());
						} catch {
							payload = { message: "Failed to export Excel" };
						}
					}
					this.showNotification(payload);
				});
		},
		downloadJson(jsonData, dataEntry) {
			const blob = new Blob([JSON.stringify(jsonData)], {
				type: "application/json",
			});

			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;

			let filename = dataEntry?.paper?.name || "Unknown paper";

			// Remove or replace characters that are problematic in filenames
			filename = filename.replace(/[<>:"/\\|?*]/g, "-");

			const maxLength = 250;
			if (filename.length > maxLength) {
				filename = filename.substring(0, maxLength);
			}

			link.download = `${filename}.json`;

			document.body.appendChild(link);

			link.click();

			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		},
		reopen() {
			this.loading = true;
			// DCTL-2137: reopen resolves reassignUserUuid as a real user uuid
			// server-side, so send the uuid (was the numeric id). selectedReassignUser
			// defaults to dataEntry.user at open, so .uuid is always available.
			const payload = this.selectedReassignUser
				? { reassignUserUuid: this.selectedReassignUser.uuid }
				: {};

			// Read before the dialog closes and clears the selection.
			const reassigned = this.reassignOnly;
			this.$http
				.post(`/data-entry/${this.selectedDataEntry.uuid}/reopen`, payload)
				.then(() => {
					this.hideReopenDialog();
					this.getCompletedExtractions();
					this.showNotification({
						message: reassigned
							? "Successfully reassigned source data extraction"
							: "Successfully re-opened source data extraction",
						status: "success",
					});
				})
				.catch((error) => {
					// Only the error path resets loading here: on success the refetch
					// above owns the spinner, and a .finally would clear it mid-fetch.
					this.loading = false;
					this.hideReopenDialog();
					this.showNotification(
						error.response?.data || {
							message: "Could not re-open the extraction.",
							status: "error",
						},
					);
				});
		},
		goToDataViewer(item) {
			this.$router.push({
				path: "/data-viewer",
				query: { sdeUuid: item.uuid },
			});
		},
		hideInvalidDialog() {
			this.invalidDialog = false;
		},
		hideReopenDialog() {
			this.reopenDialog = false;
			this.selectedReassignUser = null;
		},
		openReopenDialog(dataEntry) {
			this.selectedDataEntry = dataEntry;
			this.selectedReassignUser = dataEntry.user;
			this.reopenDialog = true;
		},
		startReadOnlyDataEntry(item) {
			this.setPaper(item.paper);
			this.setSaveUUID(item.uuid);
			// Open the read-only viewer matching the format the extraction was
			// saved in: DACTYL_SDE -> SDEv2, anything else (WPD) -> SDEv1.
			const base =
				item.data_format === "DACTYL_SDE"
					? "/source-data-extraction-read-only"
					: "/data-entry-read-only";
			this.$router.push(`${base}/${item.paper.uuid}/${item.uuid}`);
		},
		completedTimestamp(item) {
			return new Date(item.completed_at).getTime();
		},
		showHistory(dataEntry) {
			this.selectedHistoryUuid = dataEntry.uuid;
			this.$nextTick(() => {
				this.$refs.historyModal.open();
			});
		},
	},
};
</script>

<style scoped lang="scss">
:deep(.completed-extractions-table) {
	&.v-data-table .v-table__wrapper td {
		font-size: 12px;
	}

	&.v-data-table .v-table__wrapper th {
		font-size: 12px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.6);
		height: 48px;
	}
}

.cet-toolbar-search {
	min-width: 240px;
	max-width: 300px;
}

.cet-toolbar-filter {
	min-width: 200px;
	max-width: 260px;
}

.chip-button {
	border-radius: 50%;
	padding: 4px;
	transition: background-color 0.2s ease;
	cursor: pointer;

	&:hover {
		color: #f09d37;
	}
}

.custom-chip {
	padding: 4px 8px;
	align-items: stretch;
	height: auto;
}

.chip-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-left: 8px;
	width: 100%;
}

.chip-text {
	display: flex;
	flex-direction: column;
	font-size: 12px;
	line-height: 1.2;
}

.chip-header {
	margin-bottom: 1px;
	font-weight: 500;
}

.chip-subheader {
	font-size: 10px;
	color: rgba(0, 0, 0, 0.6);
}

.chip-icons {
	display: flex;
	align-items: center;
}

.info-sheet {
	background-color: rgba(0, 0, 0, 0.03);
	border: 1px solid var(--border-default);
}
</style>
